import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AppBar, Box, BottomNavigation, BottomNavigationAction,
  Fab, IconButton, Paper, Toolbar, Typography,
} from '@mui/material';
import { Add, ArrowBack, Home, NotificationsActive, Refresh, Sensors, Settings, Videocam } from '@mui/icons-material';
import { useSwipeable } from 'react-swipeable';
import { GatewayCard } from './components/GatewayCard';
import { NodeList } from './components/NodeList';
import { AlarmTab } from './components/AlarmTab';
import { SettingsPage } from './components/SettingsPage';
import { SetupScreen } from './components/SetupScreen';
import { LoginScreen } from './components/LoginScreen';
import { HousePicker } from './components/HousePicker';
import { CamerasPage } from './components/CamerasPage';
import { SensorsPage, SensorSource } from './components/SensorsPage';
import { useLanguage } from './i18n/LanguageContext';
import { useAuth } from './context/AuthContext';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { gatewaysKey, sensorSourcesKey } from './context/houseStorage';
import { Gateway } from './types';

type Tab = 'alarm' | 'gateways' | 'cameras' | 'sensors' | 'settings';

export const MAX_WIDTH = 600;

const makeNodes = (nodeDefaultLabel: (i: number) => string, nodeDefaultDescription: (i: number) => string) =>
  Array(8).fill(null).map((_, i) => ({
    label: nodeDefaultLabel(i),
    description: nodeDefaultDescription(i),
    state: null as null,
  }));

const App: React.FC = () => {
  const { isSetupDone, activeHouseId, isAuthenticated, logout } = useAuth();
  useSessionTimeout(logout, isAuthenticated);

  if (!isSetupDone) return <SetupScreen />;
  if (!activeHouseId) return <HousePicker />;
  if (!isAuthenticated) return <LoginScreen />;

  return <AuthenticatedApp key={activeHouseId} houseId={activeHouseId} />;
};

const AuthenticatedApp: React.FC<{ houseId: string }> = ({ houseId }) => {
  const { t } = useLanguage();
  const { gatewayUrl, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('alarm');
  const [selectedGateway, setSelectedGateway] = useState<Gateway | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openSettingsId, setOpenSettingsId] = useState<number | null>(null);
  const nodeConfigFetchedRef = useRef(false);

  const [gateways, setGateways] = useState<Gateway[]>(() => {
    const saved = localStorage.getItem(gatewaysKey(houseId));
    if (saved) {
      try {
        const parsed: Gateway[] = JSON.parse(saved);
        const seen = new Set<number>();
        return parsed.map((g) => {
          const id = seen.has(g.id) ? Math.max(...Array.from(seen)) + 1 : g.id;
          seen.add(id);
          return { ...g, id };
        });
      } catch { /* fall through */ }
    }
    return [{ id: 1, name: 'Gateway 1', url: '', nodes: makeNodes(t.nodeDefaultLabel, t.nodeDefaultDescription) }];
  });

  const [sensorSources, setSensorSources] = useState<SensorSource[]>(() => {
    try { return JSON.parse(localStorage.getItem(sensorSourcesKey(houseId)) ?? '[]'); } catch { return []; }
  });
  const [showCameras, setShowCameras] = useState(true);

  useEffect(() => {
    localStorage.setItem(sensorSourcesKey(houseId), JSON.stringify(sensorSources));
  }, [sensorSources, houseId]);

  useEffect(() => {
    if (!gatewayUrl) return;
    setGateways(prev => prev.map((g, i) => ({ ...g, url: g.url || `${gatewayUrl}/board/${i + 1}` })));
  }, [gatewayUrl]);

  const fetchNodeConfig = useCallback(() => {
    if (!gatewayUrl) return;
    fetch(`${gatewayUrl}/api/node-config`, { headers: getAuthHeader() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((cfg: { version: number; cameras?: boolean; gateways?: Array<{ name: string; path: string; kind?: Gateway['kind']; nodes: Array<{ index: number; label: string; type?: string; description?: string }> }>; sensorSources?: Array<{ name: string; path: string }> }) => {
        if (!cfg || cfg.version !== 1) return;
        if (cfg.cameras !== undefined) setShowCameras(cfg.cameras);

        if (Array.isArray(cfg.gateways) && cfg.gateways.length > 0) {
          setGateways(prev => {
            const next = cfg.gateways!.map((gw, i) => {
              const existing = prev[i];
              const url = `${gatewayUrl}${gw.path}`;
              const maxIndex = gw.nodes.reduce((m, cn) => Math.max(m, cn.index), -1);
              const nodeCount = maxIndex >= 0 ? maxIndex + 1 : (existing?.nodes.length ?? 8);
              const nodes = Array(nodeCount).fill(null).map((_, ni) => {
                const cfgNode = gw.nodes.find(cn => cn.index === ni);
                const ex = existing?.nodes[ni];
                const base = ex ?? { label: t.nodeDefaultLabel(ni), description: t.nodeDefaultDescription(ni), state: null as null };
                if (!cfgNode) return base;
                return { ...base, label: cfgNode.label, ...(cfgNode.type ? { type: cfgNode.type as Gateway['nodes'][0]['type'] } : {}), ...(cfgNode.description !== undefined ? { description: cfgNode.description } : {}) };
              });
              return { id: i + 1, name: gw.name, url, kind: gw.kind, nodes };
            });
            return next;
          });
        }

        if (Array.isArray(cfg.sensorSources) && cfg.sensorSources.length > 0) {
          setSensorSources(prev => {
            const fromPi: SensorSource[] = cfg.sensorSources!.map((s, i) => ({
              id: `pi-${i}`,
              name: s.name,
              url: `${gatewayUrl}${s.path}`,
            }));
            const userAdded = prev.filter(s => !s.id.startsWith('pi-'));
            return [...fromPi, ...userAdded];
          });
        }
      })
      .catch(() => { });
  }, [gatewayUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gatewayUrl || nodeConfigFetchedRef.current) return;
    nodeConfigFetchedRef.current = true;
    fetchNodeConfig();
  }, [gatewayUrl, fetchNodeConfig]);

  useEffect(() => {
    const clean = gateways.map(({ lastFetchedAt: _lfa, ...g }) => ({
      ...g,
      nodes: g.nodes.map(({ isLoading: _, error: __, ...n }) => n),
    }));
    localStorage.setItem(gatewaysKey(houseId), JSON.stringify(clean));
  }, [gateways, houseId]);

  const handleTabChange = (_: React.SyntheticEvent, value: Tab) => {
    setActiveTab(value);
    setSelectedGateway(null);
  };

  const handleAddGateway = () => {
    const newId = Math.max(...gateways.map(g => g.id), 0) + 1;
    const boardIndex = gateways.length + 1;
    setGateways([...gateways, {
      id: newId,
      name: `Gateway ${newId}`,
      url: gatewayUrl ? `${gatewayUrl}/board/${boardIndex}` : '',
      nodes: makeNodes(t.nodeDefaultLabel, t.nodeDefaultDescription),
    }]);
  };

  const handleGatewayChange = (updated: Gateway) => {
    setGateways(prev => prev.map(g => g.id === updated.id ? updated : g));
    if (selectedGateway?.id === updated.id) setSelectedGateway(updated);
  };

  const handleDeleteGateway = (id: number) => {
    setGateways(gateways.filter(g => g.id !== id));
  };

  const fabRight = `max(16px, calc((100vw - ${MAX_WIDTH}px) / 2 + 16px))`;
  const showAppBar = selectedGateway !== null || activeTab === 'gateways';

  const tabs: Tab[] = ['alarm', 'gateways', ...(showCameras ? ['cameras' as Tab] : []), 'sensors', 'settings'];
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (selectedGateway) return;
      const next = tabs[tabs.indexOf(activeTab) + 1];
      if (next) setActiveTab(next);
    },
    onSwipedRight: () => {
      if (selectedGateway) return;
      const prev = tabs[tabs.indexOf(activeTab) - 1];
      if (prev) setActiveTab(prev);
    },
    preventScrollOnSwipe: false,
    trackMouse: false,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', pt: 'env(safe-area-inset-top)' }}>

      {showAppBar && (
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.default' }}>
          <Toolbar disableGutters sx={{ px: 2 }}>
            <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', width: '100%', display: 'flex', alignItems: 'center' }}>
              {selectedGateway ? (
                <>
                  <IconButton edge="start" onClick={() => setSelectedGateway(null)} sx={{ mr: 1 }}>
                    <ArrowBack />
                  </IconButton>
                  <Typography variant="h6" sx={{ flex: 1 }}>{t.nodes}</Typography>
                  <IconButton edge="end" onClick={() => setRefreshKey(k => k + 1)}>
                    <Refresh />
                  </IconButton>
                </>
              ) : (
                <Typography variant="h6">{t.gateways}</Typography>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box {...swipeHandlers} sx={{ flex: 1, overflowY: 'auto', overscrollBehaviorY: 'contain' }}>
        {selectedGateway ? (
          <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', width: '100%' }}>
            <NodeList
              gateway={selectedGateway}
              onChange={handleGatewayChange}
              refreshKey={refreshKey}
              hideFirstNode={selectedGateway.id === gateways[0]?.id}
            />
          </Box>
        ) : activeTab === 'alarm' ? (
          <AlarmTab gateway={gateways[0] ?? null} onChange={handleGatewayChange} />
        ) : activeTab === 'cameras' ? (
          <CamerasPage />
        ) : activeTab === 'sensors' ? (
          <SensorsPage sources={sensorSources} onSourcesChange={setSensorSources} />
        ) : activeTab === 'settings' ? (
          <SettingsPage gateways={gateways} onImport={setGateways} onSyncFromPi={fetchNodeConfig} />
        ) : (
          <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', width: '100%', p: 2 }}>
            {gateways.map((gateway, i) => (
              <GatewayCard
                key={gateway.id}
                gateway={gateway}
                loadDelay={i * 100}
                onSelect={() => setSelectedGateway(gateway)}
                onDelete={gateways.length > 1 ? () => handleDeleteGateway(gateway.id) : undefined}
                onChange={handleGatewayChange}
                showSettings={openSettingsId === gateway.id}
                onShowSettingsChange={(show) => setOpenSettingsId(show ? gateway.id : null)}
              />
            ))}
            <Fab
              color="primary"
              onClick={handleAddGateway}
              sx={{ position: 'fixed', bottom: 'calc(56px + env(safe-area-inset-bottom) + 16px)', right: fabRight }}
            >
              <Add />
            </Fab>
          </Box>
        )}
      </Box>

      <Paper elevation={3} sx={{ pb: 'env(safe-area-inset-bottom)', flexShrink: 0 }}>
        <BottomNavigation value={activeTab} onChange={handleTabChange}>
          <BottomNavigationAction label={t.tabAlarm}    value="alarm"    icon={<NotificationsActive />} />
          <BottomNavigationAction label={t.tabGateways} value="gateways" icon={<Home />} />
          {showCameras && <BottomNavigationAction label={t.tabCameras}  value="cameras"  icon={<Videocam />} />}
          <BottomNavigationAction label={t.tabSensors}  value="sensors"  icon={<Sensors />} />
          <BottomNavigationAction label={t.settingsTitle} value="settings" icon={<Settings />} />
        </BottomNavigation>
      </Paper>

    </Box>
  );
};

export default App;
